import classNames from 'classnames';
import * as React from 'react';
import { CSSProperties, useMemo, useRef } from 'react';
import { CloseIcon, Edit1Icon, PlayIcon } from 'tdesign-icons-react';
import { Button } from 'tdesign-react';

import { LIMIT_VIDEO } from '@/const/form';
import { UserResourceType } from '@/interface/library';
import { EAsyncStatus, RichMediaValue } from '@/interface/media';
import { ILibraryDialogRef } from '@/pages/library/dialog';
import { useTypedSelector } from '@/store';

import { emitRichMediaFile, getRichLocalPath } from '../change-data';
import { UploadContent } from '../core/common';
import UploadAIContent from '../core/common/content/ai';
import LibraryContent from '../core/common/content/library';
import UploadDialog from '../core/dialog';
import { getVideoThumbnail } from '../core/player/utils/getThumbnail';
import styles from './video.module.less';

export interface INewUploadIProps {
  value: RichMediaValue;
  onChange: (file?: string | RichMediaValue, fileInfo?: any) => any;
  asyncUploadOnChange?: (file?: RichMediaValue) => any; // 剧情中卸载后处理图片loading恢复
  limit?: {
    fileType?: string[];
    size?: number;
    showSizeMsg?: string;
  };
  disabled?: boolean;
  promptText?: string;
  style?: CSSProperties;
  isHorizontal?: boolean;
}

export const UploadVideo: React.FC<INewUploadIProps> = (props) => {
  const {
    limit = { ...LIMIT_VIDEO, fileType: LIMIT_VIDEO.type },
    onChange,
    asyncUploadOnChange,
    value,
    style,
    disabled,
    isHorizontal = false,
  } = props;
  const { onVideoWhiteList } = useTypedSelector(store => store.user);

  const dialogRef = useRef<ILibraryDialogRef>(null);

  const loading = React.useMemo(() => {
    if (!asyncUploadOnChange) {
      return false;
    }
    if (value?.asyncStatus === EAsyncStatus.UPLOAD_LOADING) {
      return true;
    }
    return false;
  }, [value?.asyncStatus, asyncUploadOnChange]);

  const handleUploadOnchange = async (params: { url: string; name: string }) => {
    const { url, name } = params;
    emitRichMediaFile(url);
    const thumbnail = await getVideoThumbnail(url, 0);
    const localPath = getRichLocalPath(url);
    const result = {
      ...value,
      thumbnail,
      url: {
        ...value?.url,
        url,
        localPath,
        name: name || localPath,
      },
    };
    handlePropsOnchange(result); // 区分场景
  };

  const handleAiOnchange = async (val: RichMediaValue) => {
    const thumbnail = await getVideoThumbnail(val.url?.url || '', 0);
    handlePropsOnchange({ ...val, thumbnail });
  };

  const handleLibraryOnchange = async (val: RichMediaValue) => {
    let { thumbnail } = val;
    if (!thumbnail) {
      thumbnail = await getVideoThumbnail(val.url?.url || '', 0);
    }
    handlePropsOnchange({ ...val, thumbnail });
  };

  const handleDialogOnchange = (result: RichMediaValue) => {
    handlePropsOnchange(result);
  };

  const handlePropsOnchange = (result: RichMediaValue) => {
    onChange(result);
  };

  const handleDelete = () => {
    if (disabled) {
      return;
    }
    handleUploadOnchange({ url: '', name: '' });
  };

  const handleOpenDialog = () => {
    dialogRef.current?.open();
  };

  const getHeader = () => (
    <div className={styles.modalTitle}>
      <span style={{ flexGrow: 1 }}>视频编辑</span>
    </div>
  );

  const len = useMemo(() => (onVideoWhiteList ? 3 : 2), [onVideoWhiteList]);

  return (
    <div className={styles.uploadWrap} style={{ ...style }}>
      {!value?.url?.url ? (
        <div className={styles.line}>
          <div className={classNames(styles[`item_${len}`], { [styles.oneItemLoading]: loading })}>
            <UploadContent
              uploadAreaOnChange={handleUploadOnchange}
              limit={limit}
              asyncUploadOnChange={asyncUploadOnChange}
              {...props}
              text={'上传视频'}
            />
          </div>
          {!loading && onVideoWhiteList
            && <div className={styles[`item_${len}`]}>
              <UploadAIContent
                {...props}
                onChange={v => handleAiOnchange(v as RichMediaValue)}
                isHorizontal={isHorizontal}
                type={UserResourceType.RESOURCE_TYPE_VIDEO}
              />
            </div>
          }
          {!loading
            && <div className={styles[`item_${len}`]}>
              <LibraryContent
                {...props}
                onChange={e => handleLibraryOnchange(e as RichMediaValue)}
                libraryStyle={len === 2 ? { borderLeft: '1px solid #333333' } : {}}
                type={UserResourceType.RESOURCE_TYPE_VIDEO}
              />
            </div>
          }
        </div>
      ) : (
        <div className={styles.showWrap}>
          <div className={styles.leftItem} onClick={handleOpenDialog}>
            <div className={styles.playIcon}>
              <PlayIcon size={16} />
            </div>
            <div className={styles.name}>{value?.url?.name}</div>
          </div>
          <div className={styles.rightItem}>
            <Button
              icon={<Edit1Icon size={16} />}
              className={styles.opBtnSmall}
              variant="text"
              onClick={handleOpenDialog}
              disabled={disabled}
            />
            <Button
              icon={<CloseIcon size={16} />}
              className={styles.opBtnSmall}
              variant="text"
              disabled={disabled}
              onClick={handleDelete}
            />
          </div>
        </div>
      )}
      <div className={styles.uploadArea}>
        <UploadDialog
          {...props}
          dialogRef={dialogRef}
          type={UserResourceType.RESOURCE_TYPE_VIDEO}
          dialogConfig={{
            header: getHeader(),
          }}
          handleDialogOnchange={handleDialogOnchange}
        />
      </div>
    </div>
  );
};

export default UploadVideo;
